import { Activity, Building, PieChart, ShieldCheck, Target } from "lucide-react"

import { PeriodPreset } from "@/shared/lib/dates/period-range"

import {
  FinanceReportCategory,
  FinanceResource,
  ReportExportKind,
  ReportFieldKind,
  type FinanceReportEntry,
  ReportModule,
} from "@/features/reports/model/report-registry-types"

/**
 * Catalog entries backed by the finance-resource report endpoints (built for the catalog beyond the reporting
 * engine). Each maps to a `{ kind: "finance-resource" }` data source; the viewer renders the adapted result.
 */
export const FINANCE_RESOURCE_ENTRIES: FinanceReportEntry[] = [
  {
    id: "reconciliation-summary",
    module: ReportModule.Finance,
    category: FinanceReportCategory.Audit,
    icon: ShieldCheck,
    titleKey: "finance.reports.resourceEntries.reconciliationSummary.title",
    descriptionKey: "finance.reports.resourceEntries.reconciliationSummary.description",
    keywords: ["reconciliation", "audit", "proven", "pending", "accounts", "sessions"],
    dataSource: { kind: "finance-resource", resource: FinanceResource.Reconciliation },
    viewer: { layout: "result-panel", defaultView: "cards" },
    export: { kind: ReportExportKind.Unavailable, reason: "no-backend" },
  },
  {
    id: "fund-utilization",
    module: ReportModule.Finance,
    category: FinanceReportCategory.Funds,
    icon: PieChart,
    titleKey: "finance.reports.resourceEntries.fundUtilization.title",
    descriptionKey: "finance.reports.resourceEntries.fundUtilization.description",
    keywords: ["fund", "utilization", "received", "spent", "restricted", "available"],
    dataSource: { kind: "finance-resource", resource: FinanceResource.FundUtilization },
    viewer: { layout: "result-panel", defaultView: "table" },
    export: { kind: ReportExportKind.ClientCsv },
  },
  {
    id: "fund-activity",
    module: ReportModule.Finance,
    category: FinanceReportCategory.Funds,
    icon: Activity,
    titleKey: "finance.reports.resourceEntries.fundActivity.title",
    descriptionKey: "finance.reports.resourceEntries.fundActivity.description",
    keywords: ["fund", "activity", "ledger", "movements", "opening", "closing", "received", "spent"],
    configSchema: {
      fields: [
        { key: "fundId", kind: ReportFieldKind.Fund, labelKey: "finance.reports.fields.fund", required: true },
        { key: "period", kind: ReportFieldKind.Period, labelKey: "finance.reports.fields.period", defaultPreset: PeriodPreset.MonthToDate },
      ],
    },
    dataSource: { kind: "finance-resource", resource: FinanceResource.FundActivity },
    viewer: { layout: "result-panel", defaultView: "table" },
    export: { kind: ReportExportKind.ClientCsv },
  },
  {
    id: "activity-budget-performance",
    module: ReportModule.Finance,
    category: FinanceReportCategory.Budgets,
    icon: Target,
    titleKey: "finance.reports.resourceEntries.activityBudgetPerformance.title",
    descriptionKey: "finance.reports.resourceEntries.activityBudgetPerformance.description",
    keywords: ["activity", "budget", "performance", "planned", "actual", "variance"],
    configSchema: {
      fields: [{ key: "activityId", kind: ReportFieldKind.Activity, labelKey: "finance.reports.fields.activity", required: true }],
    },
    dataSource: { kind: "finance-resource", resource: FinanceResource.ActivityBudgetPerformance },
    viewer: { layout: "result-panel", defaultView: "table" },
    export: { kind: ReportExportKind.ClientCsv },
  },
  {
    id: "department-summary",
    module: ReportModule.Finance,
    category: FinanceReportCategory.Transactions,
    icon: Building,
    titleKey: "finance.reports.resourceEntries.departmentSummary.title",
    descriptionKey: "finance.reports.resourceEntries.departmentSummary.description",
    keywords: ["department", "income", "expense", "net", "summary"],
    configSchema: {
      fields: [{ key: "period", kind: ReportFieldKind.Period, labelKey: "finance.reports.fields.period", defaultPreset: PeriodPreset.MonthToDate }],
    },
    dataSource: { kind: "finance-resource", resource: FinanceResource.DepartmentSummary },
    viewer: { layout: "result-panel", defaultView: "table" },
    export: { kind: ReportExportKind.ClientCsv },
  },
]
